'use client';

import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';

import type { CoachInfo } from '../_lib/server/coaches-page.loader';

export function CoachBiography({ coach }: { coach: CoachInfo }) {
  const { t } = useTranslation('coaches');
  const details = coach.profileDetails;

  return (
    <article className="space-y-8" data-test="coach-biography">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <Avatar className="h-auto w-full shrink-0 aspect-[4/3] rounded-2xl sm:h-44 sm:w-44 sm:aspect-square">
          <AvatarImage src={coach.pictureUrl ?? undefined} alt={coach.name} className="object-cover object-top" />
          <AvatarFallback className="rounded-2xl bg-muted text-4xl font-semibold">
            {coach.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 space-y-3">
          <h1 className="break-words text-3xl font-semibold tracking-tight sm:text-4xl">{coach.name}</h1>
          {details?.title && <p className="text-lg text-muted-foreground">{details.title}</p>}
          {details?.quote && (
            <blockquote className="border-s-2 border-primary ps-4 text-lg font-medium leading-relaxed">
              {details.quote}
            </blockquote>
          )}
        </div>
      </div>

      {coach.bio && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">{t('aboutCoach', 'About')}</h2>
          <p className="whitespace-pre-line break-words leading-relaxed text-muted-foreground">{coach.bio}</p>
        </section>
      )}

      {!!details?.sections.length && (
        <div className="grid gap-4 sm:grid-cols-2">
          {details.sections.map((section, index) => (
            <section key={index} className="rounded-xl border p-5 sm:p-6">
              <h2 className="break-words text-lg font-semibold">{section.heading}</h2>
              <ul className="mt-3 list-disc space-y-2 ps-5 leading-relaxed text-muted-foreground marker:text-primary">
                {section.items.map((item, itemIndex) => <li key={itemIndex} className="break-words ps-1">{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      )}

      {coach.phoneNumber && (
        <a href={`tel:${coach.phoneNumber}`} className="inline-flex min-h-11 items-center text-sm underline underline-offset-4">
          {t('callCoach', 'Call {{name}}', { name: coach.name })}
        </a>
      )}
      {coach.instagramHandle && <p className="text-sm text-muted-foreground">@{coach.instagramHandle}</p>}
    </article>
  );
}
